<template>
  <div :class="metaBlockWrapperClassName" :style="wrappingReadSettingsStyles">
    <div
      v-if="metaView"
      ref="metaBlock"
      class="meta-info"
      :dir="metaView.direction"
    >
      <div class="meta-info-layout">
        <div class="meta-info-left-control pt-6"></div>
        <div class="flex-shrink-0 pt-6 meta-info-content">
          <div>
            <div class="meta-info-summary">
              <div class="flex-grow-0 pa-0">
                <div class="book-image-box">
                  <CompilationCover
                    :compilation-id="metaView.id"
                    :cover-size="thumbSize.XLARGE"
                    :cover-file-name="editableInfo.coverFileName"
                  ></CompilationCover>
                  <div
                    v-if="isEditMode"
                    class="book-change-cover-button"
                    @click.stop
                  >
                    <BaseButton
                      :disabled="!isOnline"
                      color="white"
                      block
                      @click="changeCompilationCover()"
                      >{{
                        $t('CompilationMetaBlock.changeCover.button.label')
                      }}</BaseButton
                    >
                  </div>
                </div>
              </div>
              <div class="meta-info-details-wrapper">
                <div class="meta-info-details">
                  <div>
                    <div class="pa-0">
                      <div class="compilation-info-updated-label">
                        {{ $t('CompilationInfo.popup.updated.label') }}
                        {{ metaView.getModifiedAtLocaleString() }}
                      </div>
                    </div>
                  </div>
                  <div>
                    <v-col class="px-0 py-2">
                      <BaseTextarea
                        v-if="isEditMode"
                        v-model.trim="editableInfo.title"
                        class="book-title"
                        :maxlength="MAX_TITLE_LEN"
                        :counter="textareaLimitCounter"
                        :error="!editableInfo.title"
                        :error-messages="titleFieldErrorMessage"
                        auto-grow
                        @focus="handleFocus"
                      />
                      <div v-if="!isEditMode" class="book-title">
                        {{ metaView.name }}
                      </div>
                    </v-col>
                  </div>
                  <div class="meta-info-stats-wrapper">
                    <div class="meta-info-stats px-0 py-2">
                      <div class="meta-info-stats-list">
                        <span v-if="metaView.audio" class="ma-0">
                          <BaseSpriteIcon
                            icon-name="ico-headset-wire"
                          ></BaseSpriteIcon>
                        </span>
                        <span class="ma-0">
                          {{ metaView.paragraphsNumber }}
                          {{ $t('CompilationMetaBlock.selections.label') }}
                        </span>
                        <span>
                          <TimeDuration
                            :duration="metaView.readingTime"
                            dir="auto"
                          />
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="meta-info-controls-wrapper">
                    <div class="pa-0 flex-grow-1">
                      <ControlsMetaBlock
                        :book-id="metaView.id"
                        :audio="metaView.audio"
                        :book-name="metaView.name"
                        :book-type="metaView.type"
                        :is-content-ready="isContentReady"
                        :disabled="isEditMode"
                        @controlsMetaBlockEvent="controlsMetaBlockEventHandler"
                      >
                        <template #custom-control>
                          <BaseButton
                            class="compilation-edit-button"
                            :disabled="isEditMode || !isOnline"
                            outlined
                            block
                            x-large
                            @click="handleEditClick()"
                          >
                            <BaseSpriteIcon
                              :icon-name="'ico-edit'"
                            ></BaseSpriteIcon>
                            {{ $t('Edit') }}
                          </BaseButton>
                        </template>
                      </ControlsMetaBlock>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div class="px-0 pb-14 pt-6">
                <div
                  v-if="metaView.showDescription || isEditMode"
                  class="meta-info-description"
                >
                  <div
                    v-if="isEditMode && editableInfo"
                    class="textarea-wrapper"
                  >
                    <BaseTextarea
                      v-model.trim="editableInfo.description"
                      :maxlength="MAX_DESCRIPTION_LEN"
                      :counter="MAX_DESCRIPTION_LEN"
                      rows="1"
                      auto-grow
                    />
                  </div>
                  <div
                    v-if="!isEditMode"
                    class="meta-info-description"
                    :style="descriptionStyles"
                  >
                    {{ metaView.description }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="meta-info-right-control pt-6 d-flex"></div>
      </div>
    </div>
  </div>
</template>

<script>
import LoggerFactory from '@/services/utils/LoggerFactory';
const logger = LoggerFactory.getLogger('CompilationMetaBlock.vue');
import AppConstantsUtil from '@/services/utils/AppConstantsUtil';

import MetablockEventEnum from '@/enums/MetablockEventEnum';
import PopupNamesEnum from '@/enums/PopupNamesEnum';
import ImageUploaderEventsEnum from '@/enums/ImageUploaderEventsEnum';
import { sizes as thumbSize } from '@shared/enums/ThumbSizesEnum';

import BaseButton from '@/components/base/BaseButton/BaseButton';
import BaseSpriteIcon from '@/components/base/BaseSpriteIcon/BaseSpriteIcon';
import BaseTextarea from '@/components/base/BaseTextarea/BaseTextarea';
import ControlsMetaBlock from '@/components/views/MetaBlock/ControlsMetaBlock/ControlsMetaBlock';
import TimeDuration from '@/components/base/TimeDuration/TimeDuration';
import CompilationCover from '@/components/base/CompilationCover/CompilationCover';

export default {
  name: 'CompilationMetaBlock',
  components: {
    BaseButton,
    BaseTextarea,
    BaseSpriteIcon,
    TimeDuration,
    ControlsMetaBlock,
    CompilationCover
  },
  props: {
    metaView: {
      type: Object,
      required: true
    },
    wrappingReadSettingsStyles: {
      type: Array,
      required: false,
      default: function() {
        return [];
      }
    },
    readSettingsStyles: {
      type: Object,
      required: false,
      default: function() {
        return {};
      }
    },
    descriptionStyles: {
      type: Object,
      required: false,
      default: function() {
        return {};
      }
    },
    isContentReady: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      MAX_TITLE_LEN: AppConstantsUtil.MAX_COMPILATION_TITLE_LENGTH,
      MAX_DESCRIPTION_LEN: AppConstantsUtil.MAX_COMPILATION_DESCRIPTION_LENGTH,
      metaBlockWrapperClassName: AppConstantsUtil.META_BLOCK_WRAPPER_CLASS,
      editableInfo: null,
      thumbSize,
      focusedElement: null
    };
  },
  computed: {
    isEditMode() {
      return this.$store.state.CompilationsStore.isEditMode;
    },
    textareaLimitCounter() {
      return this.isEditMode && this.MAX_TITLE_LEN;
    },
    isOnline() {
      return this.$store.getters['ContextStore/isOnline'];
    },
    titleFieldErrorMessage() {
      return !this.editableInfo.title
        ? this.$t('ValidationError.emptyCompilationTitle')
        : [];
    },
    editMeta() {
      return this.$store.getters['CompilationsStore/getEditMeta']();
    }
  },
  watch: {
    'editableInfo.title'(newVal, oldVal) {
      this.updateInfo(newVal, oldVal);
    },
    'editableInfo.description'(newVal, oldVal) {
      this.updateInfo(newVal, oldVal);
    },
    isEditMode(current, prev) {
      const isEditingFinished = prev === true && current === false;
      if (isEditingFinished) {
        this.editableInfo = this.metaView.getEditableInfo();
      }
    },
    'editMeta.coverFileName'(newVal) {
      this.editableInfo.coverFileName = newVal;
    }
  },
  created() {
    this.initCompilationMetaView();
  },
  mounted() {
    this.$_emitEvent(MetablockEventEnum.MOUNTED, {
      metaBlockHeigth: this.$refs.metaBlock.clientHeight
    });
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    async initCompilationMetaView() {
      this.editableInfo = this.metaView.getEditableInfo();
    },

    controlsMetaBlockEventHandler(payload) {
      const { type, data } = payload;
      this.$_emitEvent(type, data);
    },

    $_emitEvent(type, data = {}) {
      this.$emit('metablockEvent', {
        type,
        data
      });
    },

    async share() {
      const linkToShare = await this.$store.dispatch(
        'CompilationsStore/share',
        this.metaView.id
      );
      this.$store.dispatch('ManagePopupStore/openPopup', {
        name: PopupNamesEnum.SOCIAL_SHARING_POPUP,
        popupContext: { linkToShare: linkToShare }
      });
    },

    updateInfo(newVal, oldVal) {
      if (oldVal === undefined) {
        return;
      }
      this.$store.commit('CompilationsStore/updateMetaInfo', this.editableInfo);
    },

    imageUploaderEventHandler(data) {
      switch (data.type) {
        case ImageUploaderEventsEnum.SUCCESS:
          this.$store.dispatch('CompilationsStore/changeCover', {
            blob: data.file
          });
          break;
        case ImageUploaderEventsEnum.ERROR:
          logger.info('Image was not uploaded');
          break;
      }
    },

    changeCompilationCover() {
      this.$store.dispatch('ManagePopupStore/openPopup', {
        name: PopupNamesEnum.COMPILATION_COVER_CHANGER_POPUP,
        popupContext: {
          compilation: this.editableInfo
        }
      });
    },

    async handleEditClick() {
      try {
        const needUpdate = await this.$store.dispatch(
          'PublicationStore/needUpdate',
          this.metaView.id
        );
        if (needUpdate) {
          this.$_showErrorToaster();
          return;
        }
        this.$store.commit('CompilationsStore/setIsEditMode', true);
      } catch (error) {
        logger.error(`Set compilation's edit mode failed with: ${error}`);
      }
    },

    handleFocus(event) {
      this.focusedElement = event.target;
    },

    handleResize() {
      if (this.focusedElement) {
        this.focusedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        this.focusedElement = null;
      }
    },

    $_showErrorToaster() {
      const text = this.$t('Compilation.editErrorToaster.message');
      this.$store.dispatch('ManagePopupStore/openErrorToaster', { text });
    }
  }
};
</script>
